'use strict'

import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'

/**
 * Fetches route geometry for all crews in fleet mode.
 *
 * @param {{ url: string, selectedScenarioId: string, isFleetMode: boolean, focusDate: string, showFleetRoutes: boolean, availableRoutes: Array, availableCrews: Array }} config
 */
export default function useFleetRoutes ({ url, selectedScenarioId, isFleetMode, focusDate, showFleetRoutes, availableRoutes, availableCrews }) {
  const [fleetRoutes, setFleetRoutes] = useState([])
  const [fleetRoutesLoading, setFleetRoutesLoading] = useState(false)

  const fetchFleetRoutes = useCallback(async () => {
    if (!isFleetMode || !focusDate || !showFleetRoutes) {
      setFleetRoutes([])
      return
    }
    const crewNameById = new Map(availableCrews.map(c => [String(c.crewId), c.crewName]))
    const crewList = availableRoutes.length
      ? availableRoutes
        .map(r => ({
          crewId: r.crewId,
          routeId: r.routeId,
          routeName: r.routeName,
          crewName: crewNameById.get(String(r.crewId)) || r.routeName
        }))
        .filter(c => c.crewId)
      : availableCrews.map(c => ({ crewId: c.crewId, routeId: null, routeName: null, crewName: c.crewName }))
    if (!crewList.length) { setFleetRoutes([]); return }

    setFleetRoutesLoading(true)
    try {
      const results = await Promise.allSettled(
        crewList.map(c => {
          const routeUrl = selectedScenarioId
            ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${c.crewId}/route`
            : `${url}/api/hmi/territory/crews/${c.crewId}/route`
          return axios.get(routeUrl, { params: { date: focusDate } })
            .then(resp => {
              const stops = (resp.data?.stops || [])
                .map(stop => {
                  const lat = Number(stop.latitude)
                  const lng = Number(stop.longitude)
                  const aspirePropertyId = Number(stop.aspirePropertyId)
                  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(aspirePropertyId)) return null
                  return { ...stop, latitude: lat, longitude: lng, aspirePropertyId }
                })
                .filter(Boolean)

              return {
                crewId: c.crewId,
                routeId: c.routeId,
                routeName: c.routeName,
                crewName: c.crewName,
                source: resp.data?.meta?.source || null,
                mode: resp.data?.meta?.mode || null,
                stops,
                path: (resp.data?.route?.geometry?.coordinates || [])
                  .map(coord => {
                    const [lng, lat] = coord || []
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
                    return { lat, lng }
                  })
                  .filter(Boolean)
              }
            })
        })
      )
      setFleetRoutes(
        results
          .filter(r => r.status === 'fulfilled' && (r.value.path.length > 1 || r.value.stops.length > 0))
          .map(r => r.value)
      )
    } catch (_err) {
      setFleetRoutes([])
    } finally {
      setFleetRoutesLoading(false)
    }
  }, [selectedScenarioId, isFleetMode, focusDate, showFleetRoutes, availableRoutes, availableCrews, url])

  useEffect(() => {
    fetchFleetRoutes()
  }, [fetchFleetRoutes])

  return {
    fleetRoutes,
    fleetRoutesLoading,
    fetchFleetRoutes
  }
}
